import { useSession } from "hooks/useSession";
import React, { useState, useEffect } from "react";
import { fetchWrapper } from "utils/apiFetch";

function MobileRecharge() {
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [pin, setMPIN] = useState("");
  const [operator, setOperator] = useState("");
  const [circle, setCircle] = useState("");
  const [operators, setOperators] = useState([]);
  const [circles, setCircles] = useState([]);
  const [error, setError] = useState("");
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const { userid } = useSession();

  // Plan-related states
  const [planData, setPlanData] = useState(null);
  const [activeTab, setActiveTab] = useState("TOPUP");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [newPlansData, setNewPlansData] = useState(null);
  const [newPlansLoading, setNewPlansLoading] = useState(false);
  const [newSearchTerm, setNewSearchTerm] = useState("");

  // Helper function to create valid CSS selector IDs
  const createValidId = (text) => {
    return text.replace(/[^a-zA-Z0-9]/g, '_').replace(/^[0-9]/, 'tab_$&');
  };

  // Fetch Operators & Circles on page load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const opRes = await fetchWrapper("https://pro.mokshiq.in/get_operator");
        setOperators(opRes.data || []);

        const circleRes = await fetchWrapper("https://pro.mokshiq.in/get_circle");
        setCircles(circleRes.data || []);
      } catch (err) {
        console.error("Error loading operators/circles", err);
      }
    };

    fetchData();
  }, []);

  // Auto-detect Operator & Circle when mobile reaches 10 digits
  useEffect(() => {
    if (mobile.length === 10) {
      const fetchOperatorCircle = async () => {
        try {
          const lookupRes = await fetchWrapper(
            `https://pro.mokshiq.in/fetch_operator_circle?phone=${mobile}`
          );

          const lookupJson = lookupRes;

          if (lookupJson?.status && lookupJson?.data?.records) {
            const operatorName = lookupJson.data.records.Operator?.trim();
            const circleName = lookupJson.data.records.comcircle?.trim();

            console.log("Auto-detected:", { operatorName, circleName });

            const matchedOperator = operators.find(
              (op) => op.name.toLowerCase() === operatorName.toLowerCase()
            );
            if (matchedOperator) {
              setOperator(matchedOperator.name);
            }

            const matchedCircle = circles.find(
              (c) => c.name.toLowerCase() === circleName.toLowerCase()
            );
            if (matchedCircle) {
              setCircle(matchedCircle.name);
            }
          }
        } catch (err) {
          console.error("Lookup failed:", err);
        }
      };

      fetchOperatorCircle();
    } else if (mobile.length < 10) {
      setOperator("");
      setCircle("");
    }
  }, [mobile, operators, circles]);

  // Fetch plan data
  const fetchPlanData = async () => {
    if (!operator || !circle) {
      alert("Please select operator and circle first");
      return;
    }

    setLoading(true);
    try {
      const response = await fetchWrapper(
        `https://pro.mokshiq.in/fetch_recharge_plan?operator=${operator}&circle=${circle}&phone=${mobile}`
      );
      setPlanData(response.data);
    } catch (err) {
      console.error("Error fetching plans:", err);
      setPlanData({
        "TOPUP": [
          {
            "rs": "20",
            "desc": "Talktime of Rs. 14.95",
            "validity": "N/A",
            "last_update": "04-09-2025"
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setMobile(value);
  };

  const handlePlanSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom1');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  const filterPlans = (plans) => {
    if (!searchTerm) return plans;
    return plans.filter(plan =>
      plan.rs.includes(searchTerm) ||
      plan.desc.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const showToast = (message, type = "primary") => {
    const alertDiv = document.createElement("div");
    alertDiv.className = `alert alert-${type} alert-dismissible cookies-alert position-fixed top-0 end-0 m-3 shadow`;
    alertDiv.setAttribute("role", "alert");
    alertDiv.style.zIndex = 2000;

    alertDiv.innerHTML = `
      <div class="d-flex justify-content-between align-items-center">
        <p class="mb-0 flex-grow-1">${message}</p>
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `;
    document.body.appendChild(alertDiv);
    setTimeout(() => alertDiv?.remove(), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (mobile.length !== 10) {
      setError("Mobile number must be 10 digits.");
      return;
    }
    if (!amount) {
      setError("Please enter recharge amount.");
      return;
    }
    if (!operator) {
      setError("Please select operator.");
      return;
    }
    if (!circle) {
      setError("Please select circle.");
      return;
    }
    setRechargeLoading(true);
    try {
      const formData = new FormData();
      formData.append("number", mobile);
      formData.append("operator", operator);
      formData.append("circle", circle);
      formData.append("amount", amount);
      formData.append("user_id", userid);
      formData.append("pin", pin);
      formData.append("lat", "28.58990012778803");
      formData.append("long", "77.381162088311");

      const data = await fetchWrapper("https://pro.mokshiq.in/create_mobile_recharge", {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      if (data.status) {
        showToast(data.message || "Success!", "success");
        setMobile("");
        setAmount("");
        setOperator("");
        setCircle("");
        setMPIN("");
        setError("");
      } else {
        showToast(data.message || "Something went wrong!", "danger");
      }
    } catch (err) {
      console.error("Recharge API Error:", err);
      showToast("Something went wrong!", "danger");
    } finally {
      setRechargeLoading(false);
    }
  };

  const fetchNewPlans = async () => {
    if (!operator || !circle) {
      alert("Please select operator and circle first");
      return;
    }

    setNewPlansLoading(true);
    try {
      const response = await fetchWrapper(`https://pro.mokshiq.in/recharge_roffer?operator=${operator}&circle=${circle}&phone=${mobile}`);
      if (response.status) {
        setNewPlansData(response.data);
      } else {
        setNewPlansData([]);
      }
    } catch (err) {
      console.error("Error fetching new plans:", err);
      setNewPlansData([]);
    } finally {
      setNewPlansLoading(false);
    }
  };

  const filterNewPlans = (plans) => {
    if (!newSearchTerm) return plans;
    return plans.filter(plan =>
      plan.rs.includes(newSearchTerm) ||
      plan.desc.toLowerCase().includes(newSearchTerm.toLowerCase())
    );
  };

  const handleNewPlanSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom2');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  return (
    <main style={{ backgroundColor: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container-fluid px-4 py-3">

        {/* Loader Overlay */}
        {rechargeLoading && (
          <div
            className="page-loader-overlay d-flex justify-content-center align-items-center"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 3000,
            }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Header Title Bar & Right User Avatar Button */}
        <div className="row align-items-center mb-3">
          <div className="col d-flex align-items-center justify-content-between">
            <div>
              <h3 className="fw-extrabold mb-1" style={{ color: "#0f172a", fontSize: "24px", letterSpacing: "-0.5px" }}>
                Mobile Recharge
              </h3>
              <ul className="d-flex align-items-center gap-2 list-unstyled mb-0" style={{ fontSize: "13.5px" }}>
                <li>
                  <a className="text-decoration-none d-flex align-items-center gap-1.5" href="#" style={{ color: "#64748b", fontWeight: "500" }}>
                    <i className="ph-duotone ph-house-line" style={{ fontSize: "16px", color: "#7e57c2" }}></i>
                  </a>
                </li>
                <li style={{ color: "#94a3b8" }}>/</li>
                <li style={{ color: "#64748b", fontWeight: "500" }}>Services</li>
                <li style={{ color: "#94a3b8" }}>/</li>
                <li className="fw-bold" style={{ color: "#7e57c2" }}>
                  Mobile Recharge
                </li>
              </ul>
            </div>

            <div>
              <button
                className="btn d-flex align-items-center justify-content-center p-0 border-0 text-white shadow-sm"
                style={{
                  backgroundColor: "#7e57c2",
                  borderRadius: "14px",
                  width: "44px",
                  height: "44px",
                  boxShadow: "0 4px 12px rgba(126, 87, 194, 0.3)"
                }}
              >
                <i className="ph-bold ph-user" style={{ fontSize: "20px" }}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Compact Hero Banner Card */}
        <div
          className="card border-0 mb-4 position-relative overflow-hidden shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #eef2ff 60%, #e0e7ff 100%)",
            borderRadius: "16px"
          }}
        >
          <div className="card-body p-3.5 px-4 position-relative z-1">
            <div className="row align-items-center">
              <div className="col-12 col-md-7 col-lg-8">
                <div className="d-flex align-items-center gap-3.5">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "14px",
                      backgroundColor: "#e0e7ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-device-mobile" style={{ fontSize: "24px" }}></i>
                  </div>
                  <div>
                    <h5 className="fw-extrabold mb-1" style={{ color: "#0f172a", fontSize: "18px" }}>
                      Mobile Recharge
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
                      Recharge your mobile number quickly and securely.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-5 col-lg-4 text-end d-none d-md-block">
                <img
                  src="/assets/images/service/mobile-recharge-banner.png"
                  onError={(e) => { e.target.src = "/assets/images/service/mobile-recharge-banner.png"; }}
                  alt="Mobile Recharge Graphic"
                  className="img-fluid"
                  style={{
                    maxHeight: "75px",
                    objectFit: "contain",
                    mixBlendMode: "multiply"
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Section: Left Form Card + Right Instant Recharge Promo Card */}
        <div className="row g-4">
          
          {/* Left Side (col-lg-8): Form Card */}
          <div className="col-12 col-lg-8">
            <div
              className="card border-0 shadow-sm h-100"
              style={{ borderRadius: "20px", backgroundColor: "#ffffff" }}
            >
              <div className="card-body p-4 p-md-4.5">
                
                {/* Form Header */}
                <div className="d-flex align-items-center gap-3.5 mb-4 pb-2 border-bottom border-light-subtle">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "14px",
                      backgroundColor: "#f3e8ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-device-mobile" style={{ fontSize: "24px" }}></i>
                  </div>
                  <div>
                    <h5 className="fw-extrabold mb-0.5" style={{ color: "#0f172a", fontSize: "17.5px" }}>
                      Recharge Details
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      Enter mobile number, select operator & circle to recharge.
                    </p>
                  </div>
                </div>

                {/* Form Elements */}
                <form onSubmit={handleSubmit}>
                  <div className="row g-3.5">

                    {/* Mobile Number Input */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold small text-dark mb-1.5" style={{ fontSize: "13px" }}>
                        Mobile Number
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0 border-light-subtle ps-3" style={{ borderRadius: "12px 0 0 12px" }}>
                          <i className="ph-bold ph-phone-call text-muted" style={{ fontSize: "16px" }}></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 border-end-0 border-light-subtle py-2 px-2"
                          placeholder="Enter 10 digit mobile number"
                          value={mobile}
                          onChange={handleMobileChange}
                          required
                          maxLength="10"
                          style={{ fontSize: "13.5px" }}
                        />
                        <span className="input-group-text bg-white border-start-0 border-light-subtle pe-3" style={{ borderRadius: "0 12px 12px 0" }}>
                          <i className="ph-duotone ph-address-book text-primary" style={{ fontSize: "18px", cursor: "pointer" }}></i>
                        </span>
                      </div>
                    </div>

                    {/* Recharge Amount Input + Sub Action Buttons */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold small text-dark mb-1.5" style={{ fontSize: "13px" }}>
                        Recharge Amount (₹)
                      </label>
                      <div className="input-group mb-2">
                        <span className="input-group-text bg-white border-end-0 border-light-subtle ps-3" style={{ borderRadius: "12px 0 0 12px" }}>
                          <span className="fw-bold text-muted" style={{ fontSize: "15px" }}>₹</span>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 border-light-subtle py-2 px-3"
                          placeholder="Enter amount (e.g. 199)"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                          style={{ borderRadius: "0 12px 12px 0", fontSize: "13.5px" }}
                        />
                      </div>

                      {/* Fetch R Offer & Fetch Plan Buttons */}
                      <div className="d-flex align-items-center gap-3 mt-2">
                        <button
                          type="button"
                          className="btn fetch-pill-btn-roffer d-inline-flex align-items-center justify-content-center gap-1.5"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvasBottom2"
                          onClick={fetchNewPlans}
                        >
                          <div className="icon-circle-roffer">
                            <i className="ph-bold ph-lightning text-white"></i>
                          </div>
                          <div className="divider-line-roffer"></div>
                          <span className="btn-label-roffer">Fetch R Offer</span>
                          <i className="ph-bold ph-caret-right chevron-roffer"></i>
                        </button>

                        <button
                          type="button"
                          className="btn fetch-pill-btn-plan d-inline-flex align-items-center justify-content-center gap-1.5"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvasBottom1"
                          onClick={fetchPlanData}
                        >
                          <div className="icon-circle-plan">
                            <i className="ph-bold ph-clipboard-text text-white"></i>
                          </div>
                          <div className="divider-line-plan"></div>
                          <span className="btn-label-plan">Fetch Plan</span>
                          <i className="ph-bold ph-caret-right chevron-plan"></i>
                        </button>
                      </div>
                    </div>

                    {/* Select Operator Dropdown */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold small text-dark mb-1.5" style={{ fontSize: "13px" }}>
                        Select Operator
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0 border-light-subtle ps-3" style={{ borderRadius: "12px 0 0 12px" }}>
                          <i className="ph-bold ph-broadcast text-muted" style={{ fontSize: "16px" }}></i>
                        </span>
                        <select
                          className="form-select border-start-0 border-light-subtle py-2 px-3"
                          value={operator}
                          onChange={(e) => setOperator(e.target.value)}
                          required
                          style={{ borderRadius: "0 12px 12px 0", fontSize: "13.5px" }}
                        >
                          <option value="">-- Select Operator --</option>
                          {operators.map((op, i) => (
                            <option key={i} value={op.name}>
                              {op.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Select Circle Dropdown */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold small text-dark mb-1.5" style={{ fontSize: "13px" }}>
                        Select Circle
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0 border-light-subtle ps-3" style={{ borderRadius: "12px 0 0 12px" }}>
                          <i className="ph-bold ph-map-pin text-muted" style={{ fontSize: "16px" }}></i>
                        </span>
                        <select
                          className="form-select border-start-0 border-light-subtle py-2 px-3"
                          value={circle}
                          onChange={(e) => setCircle(e.target.value)}
                          required
                          style={{ borderRadius: "0 12px 12px 0", fontSize: "13.5px" }}
                        >
                          <option value="">-- Select Circle --</option>
                          {circles.map((c, i) => (
                            <option key={i} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Transaction Pin Input */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold small text-dark mb-1.5" style={{ fontSize: "13px" }}>
                        Transaction Pin
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0 border-light-subtle ps-3" style={{ borderRadius: "12px 0 0 12px" }}>
                          <i className="ph-bold ph-lock text-muted" style={{ fontSize: "16px" }}></i>
                        </span>
                        <input
                          type={showPin ? "text" : "password"}
                          className="form-control border-start-0 border-end-0 border-light-subtle py-2 px-2"
                          placeholder="Enter transaction pin"
                          value={pin}
                          onChange={(e) => setMPIN(e.target.value)}
                          required
                          style={{ fontSize: "13.5px" }}
                        />
                        <span
                          className="input-group-text bg-white border-start-0 border-light-subtle pe-3"
                          onClick={() => setShowPin(!showPin)}
                          style={{ borderRadius: "0 12px 12px 0", cursor: "pointer" }}
                        >
                          <i className={`ph-bold ${showPin ? "ph-eye" : "ph-eye-slash"} text-muted`} style={{ fontSize: "16px" }}></i>
                        </span>
                      </div>
                    </div>

                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="alert alert-danger border-0 mt-3 py-2 px-3 rounded-3 small">
                      <i className="ph-bold ph-warning-circle me-1.5"></i>
                      {error}
                    </div>
                  )}

                  {/* Submit & Reset Action Buttons */}
                  <div className="row g-3 mt-4 pt-2">
                    <div className="col-12 col-md-6">
                      <button
                        type="submit"
                        className="btn border-0 text-white w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                        style={{
                          borderRadius: "12px",
                          backgroundColor: "#7e57c2",
                          fontSize: "14px",
                          boxShadow: "0 4px 12px rgba(126, 87, 194, 0.25)"
                        }}
                      >
                        <i className="ph-bold ph-lightning" style={{ fontSize: "16px" }}></i>
                        <span>Submit</span>
                      </button>
                    </div>

                    <div className="col-12 col-md-6">
                      <button
                        type="button"
                        className="btn btn-light border-1 border-light-subtle text-secondary w-100 py-2.5 fw-semibold d-flex align-items-center justify-content-center gap-2"
                        onClick={() => {
                          setMobile("");
                          setAmount("");
                          setOperator("");
                          setCircle("");
                          setMPIN("");
                          setError("");
                        }}
                        style={{ borderRadius: "12px", fontSize: "14px", backgroundColor: "#f1f5f9" }}
                      >
                        <i className="ph-bold ph-arrows-clockwise" style={{ fontSize: "16px" }}></i>
                        <span>Reset</span>
                      </button>
                    </div>
                  </div>

                </form>

              </div>
            </div>
          </div>

          {/* Right Side (col-lg-4): Instant Mobile Recharge Promo Side Card */}
          <div className="col-12 col-lg-4">
            <div
              className="card border-0 shadow-sm h-100 text-center overflow-hidden position-relative"
              style={{
                borderRadius: "20px",
                background: "linear-gradient(180deg, #f5f3ff 0%, #ede9fe 100%)"
              }}
            >
              <div className="card-body p-4 d-flex flex-column align-items-center justify-content-between">
                
                {/* Header Badge & Title */}
                <div className="text-center pt-2">
                  <div
                    className="d-inline-flex align-items-center justify-content-center mb-2.5 shadow-sm"
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "50%",
                      backgroundColor: "#7e57c2",
                      color: "#ffffff"
                    }}
                  >
                    <i className="ph-duotone ph-device-mobile" style={{ fontSize: "26px" }}></i>
                  </div>
                  <h4 className="fw-extrabold mb-1" style={{ color: "#0f172a", fontSize: "19px" }}>
                    Instant Mobile Recharge
                  </h4>
                  <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                    Fast, Secure & Hassle Free
                  </p>
                </div>

                {/* 3D Phone Graphic */}
                <div className="my-4 position-relative px-3 w-100">
                  <img
                    src="/assets/images/service/mobile-recharge-side.png"
                    onError={(e) => { e.target.src = "/assets/images/service/mobile-recharge-side.png"; }}
                    alt="Instant Mobile Recharge Graphic"
                    className="img-fluid"
                    style={{
                      maxHeight: "260px",
                      objectFit: "contain",
                      filter: "drop-shadow(0 10px 15px rgba(126, 87, 194, 0.15))"
                    }}
                  />
                </div>

                <div className="pb-1 text-center">
                  <span className="badge bg-white text-primary border border-primary-subtle rounded-pill px-3 py-1.5 fw-semibold shadow-2k" style={{ fontSize: "12px", color: "#7e57c2" }}>
                    ⚡ Powered by Mokshiq
                  </span>
                </div>

              </div>
            </div>
          </div>

        </div>

        {/* Offcanvas for Plan Selection */}
        <div
          className="offcanvas offcanvas-bottom rounded-top-4 border-0 shadow-lg"
          id="offcanvasBottom1"
          tabIndex="-1"
          style={{ height: '75vh', maxWidth: '800px', margin: '0 auto', backgroundColor: '#ffffff' }}
        >
          <div className="offcanvas-header border-bottom py-3 px-4 bg-white sticky-top">
            <h5 className="offcanvas-title fw-bold text-dark mb-0">Select Recharge Plan</h5>
            <button
              className="btn-close text-secondary"
              data-bs-dismiss="offcanvas"
              type="button"
              aria-label="Close"
            ></button>
          </div>
          <div className="offcanvas-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted small">Fetching available plans...</p>
              </div>
            ) : planData ? (
              <>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control form-control-lg bg-white border shadow-sm fs-6"
                    placeholder="🔍 Search plans by amount or keyword..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="overflow-auto mb-3">
                  <ul className="nav nav-pills flex-nowrap gap-2 pb-2">
                    {Object.keys(planData).map((category) => {
                      const validId = createValidId(category);
                      const isAct = activeTab === category;
                      return (
                        <li className="nav-item" key={category}>
                          <button
                            className={`nav-link text-nowrap py-2 px-3 rounded-pill fw-semibold border ${
                              isAct ? 'btn-primary text-white border-primary shadow-sm' : 'bg-white text-dark border-light-subtle'
                            }`}
                            data-bs-toggle="tab"
                            data-bs-target={`#${validId}`}
                            type="button"
                            onClick={() => setActiveTab(category)}
                            style={{ fontSize: '13px' }}
                          >
                            {category.replace(/[^a-zA-Z]/g, ' ')}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="tab-content">
                  {Object.entries(planData).map(([category, plans]) => {
                    const validId = createValidId(category);
                    return (
                      <div
                        key={category}
                        className={`tab-pane fade ${activeTab === category ? 'show active' : ''}`}
                        id={validId}
                      >
                        <div className="d-flex flex-column gap-3">
                          {filterPlans(plans).map((plan, index) => (
                            <div
                              key={index}
                              className="card plan-card border shadow-sm rounded-3 overflow-hidden bg-white"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handlePlanSelect(plan.rs)}
                            >
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center gap-3">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <h5 className="fw-bold text-primary mb-0">
                                        ₹{plan.rs}
                                      </h5>
                                    </div>
                                    <p className="card-text text-secondary small mb-2 lh-sm">
                                      {plan.desc}
                                    </p>
                                    <div className="d-flex align-items-center gap-3">
                                      <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                                        Validity: {plan.validity}
                                      </span>
                                      {plan.last_update && (
                                        <span className="text-muted extra-small">
                                          Updated: {plan.last_update}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    className="btn btn-outline-primary px-3 rounded-pill fw-semibold flex-shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePlanSelect(plan.rs);
                                    }}
                                  >
                                    Select
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {filterPlans(plans).length === 0 && (
                          <div className="text-center py-5 text-muted">
                            No plans found matching your search.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-5 text-muted">
                Click "Fetch Plan" to load available plans.
              </div>
            )}
          </div>
        </div>

        {/* Offcanvas for New Plans */}
        <div
          className="offcanvas offcanvas-bottom rounded-top-4 border-0 shadow-lg"
          id="offcanvasBottom2"
          tabIndex="-1"
          style={{ height: '75vh', maxWidth: '800px', margin: '0 auto', backgroundColor: '#ffffff' }}
        >
          <div className="offcanvas-header border-bottom py-3 px-4 bg-white sticky-top">
            <h5 className="offcanvas-title fw-bold text-dark mb-0">Select Data / R-Offer Pack</h5>
            <button
              className="btn-close text-secondary"
              data-bs-dismiss="offcanvas"
              type="button"
              aria-label="Close"
            ></button>
          </div>
          <div className="offcanvas-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
            {newPlansLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted small">Fetching R-Offers...</p>
              </div>
            ) : newPlansData && newPlansData.length > 0 ? (
              <>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control form-control-lg bg-white border shadow-sm fs-6"
                    placeholder="🔍 Search offers by amount or description..."
                    value={newSearchTerm}
                    onChange={(e) => setNewSearchTerm(e.target.value)}
                  />
                </div>

                <div className="d-flex flex-column gap-3">
                  {filterNewPlans(newPlansData).map((plan, index) => (
                    <div
                      key={index}
                      className="card plan-card border shadow-sm rounded-3 overflow-hidden bg-white"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNewPlanSelect(plan.rs)}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center gap-3">
                          <div className="flex-grow-1">
                            <h5 className="fw-bold text-primary mb-1">
                              ₹{plan.rs}
                            </h5>
                            <p className="card-text text-secondary small mb-0 lh-sm">
                              {plan.desc}
                            </p>
                          </div>
                          <button
                            className="btn btn-outline-primary px-3 rounded-pill fw-semibold flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNewPlanSelect(plan.rs);
                            }}
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {filterNewPlans(newPlansData).length === 0 && (
                  <div className="text-center py-5 text-muted">
                    No offers found matching your search.
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5 text-muted">
                Click "Fetch R Offer" to load available offers.
              </div>
            )}
          </div>
        </div>

      </div>

      <style jsx>{`
        /* R Offer Pill Button */
        .fetch-pill-btn-roffer {
          background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%) !important;
          border: 1.5px solid #93c5fd !important;
          border-radius: 50px !important;
          height: 26px !important;
          padding: 1px 8px !important;
          width: 125px !important;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
          box-shadow: 0 2px 5px rgba(59, 130, 246, 0.1) !important;
        }
        .fetch-pill-btn-roffer:hover {
          background: linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%) !important;
          border-color: #3b82f6 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 10px rgba(59, 130, 246, 0.2) !important;
        }
        .icon-circle-roffer {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8.5px;
          flex-shrink: 0;
          box-shadow: 0 1px 3px rgba(37, 99, 235, 0.3);
        }
        .divider-line-roffer {
          width: 1px;
          height: 10px;
          background-color: #cbd5e1;
          flex-shrink: 0;
        }
        .btn-label-roffer {
          font-size: 10px;
          font-weight: 700;
          color: #1d4ed8;
          white-space: nowrap;
        }
        .chevron-roffer {
          font-size: 9.5px;
          color: #3b82f6;
          flex-shrink: 0;
        }

        /* Fetch Plan Pill Button */
        .fetch-pill-btn-plan {
          background: linear-gradient(135deg, #ffffff 0%, #faf5ff 100%) !important;
          border: 1.5px solid #d8b4fe !important;
          border-radius: 50px !important;
          height: 26px !important;
          padding: 1px 8px !important;
          width: 125px !important;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
          box-shadow: 0 2px 5px rgba(147, 51, 234, 0.1) !important;
        }
        .fetch-pill-btn-plan:hover {
          background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%) !important;
          border-color: #a855f7 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 10px rgba(147, 51, 234, 0.2) !important;
        }
        .icon-circle-plan {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, #a855f7 0%, #7e57c2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8.5px;
          flex-shrink: 0;
          box-shadow: 0 1px 3px rgba(126, 87, 194, 0.3);
        }
        .divider-line-plan {
          width: 1px;
          height: 10px;
          background-color: #e2e8f0;
          flex-shrink: 0;
        }
        .btn-label-plan {
          font-size: 10px;
          font-weight: 700;
          color: #6b21a8;
          white-space: nowrap;
        }
        .chevron-plan {
          font-size: 9.5px;
          color: #a855f7;
          flex-shrink: 0;
        }

        .plan-card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          transform: translateY(-2px);
          transition: all 0.3s ease;
        }
        
        .offcanvas-body {
          max-height: calc(80vh - 60px);
          overflow-y: auto;
        }
      `}</style>

    </main>
  );
}

export default MobileRecharge;
